import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WhatsAppButton } from '../../components/WhatsApp.jsx';
import CustomerWhatsApp from './CustomerWhatsApp.jsx';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, getToken } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import { isStaff } from '../account/AccountLayout.jsx';
import { displayPhone } from '../../phone.js';
import { Modal, StatusBadge, CUSTOMER_STATUSES, SOURCES, countryName, fmtDate } from './ws.jsx';
import { NewContactModal, useContactTypes, typeLabel } from './Customers.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

const ICON = { note: '📝', file: '📎', status: '🔁', assign: '👤', email: '✉️', document: '📄', chat: '💬', quote: '🧾', whatsapp: '🟢', wamsg: '🟢' };

// One customer: details, status, follow-up, tags, who handles it, and the
// timeline (chats, emails, documents, quote requests, notes, files).
export default function CustomerPage({ admin = false, base = '/account/workspace' }) {
  const { id } = useParams();
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const contactTypes = useContactTypes();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // 'email' | 'note' | 'task' | 'edit' | 'link' | 'showcase'
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);
  // WhatsApp tab (migration 043, Business API): shown when usable for this person.
  const [params, setParams] = useSearchParams();
  const [wa, setWa] = useState(null);
  useEffect(() => { if (!admin) api('/workspace/whatsapp/status').then(setWa).catch(() => setWa(null)); }, [admin]);
  const tab = params.get('tab') === 'whatsapp' && wa?.usable ? 'whatsapp' : 'timeline';
  const load = useCallback(() => api(`/workspace/customers/${id}`).then((x) => { setD(x); setError(''); }).catch((e) => setError(errorText(e))), [id, errorText]);
  useEffect(() => { load(); }, [load]);
  const say = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  async function patch(body) { try { await api(`/workspace/customers/${id}`, { method: 'PATCH', body }); load(); } catch (e) { setError(errorText(e)); } }
  async function chat() {
    try {
      if (admin) { const r = await api('/admin/chat/start', { method: 'POST', body: { userId: d.customer.id } }); navigate(`/admin/messages/${r.id}`); return; }
      if (isStaff(user)) { const r = await api('/workspace/chat/start', { method: 'POST', body: { userId: d.customer.id } }); navigate(`/account/workspace/messages/${r.id}`); return; }
      const cid = d.conversationId || (await api('/chat/conversations', { method: 'POST', body: { userId: d.customer.id } })).id;
      navigate(`/account/messages/${cid}`);
    } catch (e) { setError(errorText(e)); }
  }
  async function upload(files) {
    if (!files?.length) return;
    const fd = new FormData(); [...files].forEach((f) => fd.append('files', f));
    try { await api(`/workspace/customers/${id}/files`, { method: 'POST', form: fd }); say(t('ws.cp.fileAdded')); load(); } catch (e) { setError(errorText(e)); }
  }
  async function task(x, body) { await api(`/workspace/tasks/${x.id}`, { method: 'PATCH', body }).catch(() => {}); load(); }
  async function invite() { try { await api(`/workspace/customers/${id}/invite`, { method: 'POST' }); say(t('ws.priv.invited')); load(); } catch (e) { setError(errorText(e)); } }
  async function archive(on) { try { await api(`/workspace/customers/${id}`, { method: 'PATCH', body: { archived: on } }); navigate(base === '/account/workspace' ? `${base}/customers` : `${base}/customers`); } catch (e) { setError(errorText(e)); } }
  async function openFile(path, name) {
    const r = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) return;
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a'); a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.download = name || ''; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  if (!d) return <div className="ws-page">{error ? <Alert>{error}</Alert> : <p className="muted">{t('common.loading')}</p>}</div>;
  const c = d.customer; const crm = d.crm;
  return (
    <div className="ws-page">
      <p className="small"><Link to={`${base}/customers`}>← {t('ws.cp.back')}</Link></p>
      <div className="ws-cust-head">
        {c.logoUrl ? <img src={c.logoUrl} alt="" /> : <span className="ws-cust-logo" aria-hidden="true">{(c.company || c.contact || '?').slice(0, 1)}</span>}
        <div className="ws-grow">
          <h1 style={{ margin: 0 }}>{c.company || c.contact}</h1>
          <TutorialSlot place="ws.customer.page" />
          <p className="muted" style={{ margin: '2px 0' }}>{[c.company && c.contact, c.city, c.country && countryName(c.country, lang)].filter(Boolean).join(' · ')}</p>
          {c.sectors.length > 0 && <p className="small" style={{ margin: 0 }}>{c.sectors.map((s) => (lang === 'en' ? s.en : s.fr)).join(' · ')}</p>}
          <span className={`ws-badge ${c.isMember ? 'ws-crm-won' : 'ws-crm-prospect'}`}>{t(c.isMember ? 'ws.cust.sourceMember' : 'ws.cust.sourcePrivate')}</span>
        </div>
        <div className="ws-row">
          {c.isMember
            ? <button type="button" className="btn btn-primary btn-sm" onClick={chat}>💬 {t('ws.cp.chat')}</button>
            : (
              <>
                {d.rights.invite && !crm.invitedAt && <button type="button" className="btn btn-primary btn-sm" onClick={invite}>📨 {t('ws.priv.invite')}</button>}
                {d.rights.invite && crm.invitedAt && <span className="small muted">{t('ws.priv.invitedAt', { date: fmtDate(crm.invitedAt, lang) })}</span>}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal('link')}>🔗 {t('ws.priv.linkBtn')}</button>
              </>
            )}
          {c.whatsapp && <WhatsAppButton small number={c.whatsapp} text={t('wa.customerMessage', { name: c.contact || c.company || '' })} onOpen={() => { api(`/workspace/whatsapp/customers/${crm.id}/opened`, { method: 'POST' }).then(load).catch(() => {}); }} />}
          {d.rights.email && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal('email')}>✉️ {t('ws.cp.email')}</button>}
          {d.rights.docs && <Link className="btn btn-ghost btn-sm" to={`${base}/documents/new?crmId=${crm.id}`}>📄 {t('ws.cp.document')}</Link>}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal('showcase')}>🏳 {t('ws.showcase.btn')}</button>
          {!c.isMember && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal('edit')}>✏️ {t('common.edit')}</button>}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => archive(!crm.archived)}>{crm.archived ? `↩ ${t('ws.priv.unarchive')}` : `🗄 ${t('ws.priv.archive')}`}</button>
        </div>
      </div>
      <Alert>{error}</Alert>
      {msg && <p className="callout callout-success" role="status">{msg}</p>}
      <div className="ws-cols">
        <section className="ws-card">
          {wa?.usable && (
            <div className="wa-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'timeline'} className={tab === 'timeline' ? 'on' : ''} onClick={() => setParams({}, { replace: true })}>{t('ws.cp.timeline')}</button>
              <button type="button" role="tab" aria-selected={tab === 'whatsapp'} className={tab === 'whatsapp' ? 'on' : ''} onClick={() => setParams({ tab: 'whatsapp' }, { replace: true })}>🟢 WhatsApp</button>
            </div>
          )}
          {tab === 'whatsapp' ? <CustomerWhatsApp crmId={crm.id} /> : (<>
          <div className="ws-row">
            <h2 className="ws-grow">{t('ws.cp.timeline')}</h2>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal('note')}>📝 {t('ws.cp.note')}</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📎 {t('ws.cp.file')}</button>
            <input ref={fileRef} type="file" multiple className="sr-only" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
          </div>
          <ol className="ws-timeline">
            {d.timeline.map((x) => (
              <li key={`${x.type}${x.id}`} className={`ws-tl ws-tl-${x.type}`}>
                <span className="ws-tl-icon" aria-hidden="true">{ICON[x.type]}</span>
                <div className="ws-tl-body">
                  <div className="ws-tl-meta"><strong>{x.type === 'email' && x.direction === 'in' ? t('ws.tl.emailIn') : t(`ws.tl.${x.type}`)}</strong><span>{fmtDate(x.at, lang, true)}</span>{x.by && <span>{x.by}</span>}{x.type === 'document' && <StatusBadge kind="doc" value={x.status} />}{x.type === 'quote' && x.status && <span className="ws-badge">{t(`ws.req.status.${x.status}`)}</span>}</div>
                  {x.type === 'email' && <><p><strong>{x.subject}</strong>{x.status === 'failed' && <span className="field-error"> — {t('ws.cp.failed')}</span>}</p><div className="ws-tl-mail" dangerouslySetInnerHTML={{ __html: x.body }} />
                    {x.attachments.length > 0 && <p className="small">{x.attachments.map((a) => <button key={a.i} type="button" className="btn btn-link btn-sm" onClick={() => openFile(`/workspace/emails/${x.id}/attachments/${a.i}`, a.name)}>📎 {a.name}</button>)}</p>}
                    <p className="small muted">{x.direction === 'in' ? t('ws.cp.receivedBy', { email: x.to }) : x.via === 'own' ? t('ws.cp.sentFrom', { email: x.from }) : t('ws.cp.replyTo', { email: x.replyTo })}</p></>}
                  {x.type === 'document' && <p><Link to={`${base}/documents/${x.id}`}>{x.ref} — {x.title}</Link>{x.responseName && <span className="small muted"> · {x.responseName}</span>}</p>}
                  {x.type === 'chat' && <p>{x.fromCustomer ? '← ' : '→ '}{x.body}</p>}
                  {x.type === 'quote' && <p>{x.title}{x.quantity ? ` — ${Number(x.quantity)} ${x.unit || ''}` : ''}</p>}
                  {x.type === 'note' && <p>{x.body}</p>}
                  {x.type === 'whatsapp' && <p>{['opened', 'optin', 'optout'].includes(x.body) ? t(`wa.tl.${x.body}`) : x.body}</p>}
                  {x.type === 'wamsg' && <p>{x.direction === 'in' ? '← ' : '→ '}{x.kind === 'template' && <span className="ws-badge">{t('wa.template')}</span>} {x.file && `📎 ${x.file} `}{x.body}{x.status === 'failed' && <span className="field-error"> — {t('wa.failed')}</span>}</p>}
                  {x.type === 'status' && <p><StatusBadge kind="crm" value={x.body} /></p>}
                  {x.type === 'assign' && <p>{d.assignees.find((a) => String(a.id) === x.body)?.name || t('ws.cust.unassigned')}</p>}
                  {x.type === 'file' && <p><button type="button" className="btn btn-link btn-sm" onClick={() => openFile(`/workspace/files/${x.file.id}`, x.file.name)}>📎 {x.file.name}</button>{x.body && ` — ${x.body}`}</p>}
                </div>
              </li>
            ))}
            {!d.timeline.length && <li className="muted small">{t('ws.cp.noTimeline')}</li>}
          </ol>
          </>)}
        </section>
        <aside className="ws-card">
          <h2>{t('ws.cp.follow')}</h2>
          <label className="field"><span className="field-label">{t('ws.cust.status')}</span>
            <select value={crm.status} onChange={(e) => patch({ status: e.target.value })}>{CUSTOMER_STATUSES.map((s) => <option key={s} value={s}>{t(`ws.crm.${s}`)}</option>)}</select></label>
          <label className="field"><span className="field-label">{t('ws.cust.followup')}</span>
            <input type="date" value={crm.nextFollowup || ''} onChange={(e) => patch({ nextFollowup: e.target.value })} /></label>
          <label className="field"><span className="field-label">{t('ws.cp.tags')}</span>
            <input defaultValue={crm.tags.join(', ')} onBlur={(e) => patch({ tags: e.target.value })} placeholder={t('ws.cp.tagsPh')} /></label>
          <label className="field"><span className="field-label">{t('ws.cust.assignee')}</span>
            <select value={crm.assignedTo?.id || ''} disabled={!d.canAssign} onChange={(e) => patch({ assignedTo: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{t('ws.cust.unassigned')}</option>{d.assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
          <dl className="ws-kv">
            {c.title && <><dt>{t('ws.priv.jobTitle')}</dt><dd>{c.title}</dd></>}
            {c.email && <><dt>{t('ws.cp.emailAddr')}</dt><dd><a href={`mailto:${c.email}`}>{c.email}</a></dd></>}
            {c.phone && <><dt>{t('ws.cp.phone')}</dt><dd>{displayPhone(c.phone)}</dd></>}
            {c.whatsapp && <><dt>{t('ws.priv.whatsapp')}</dt><dd>{c.whatsapp}</dd></>}
            {c.address && <><dt>{t('ws.cp.address')}</dt><dd>{c.address}</dd></>}
            {c.website && <><dt>{t('ws.cp.website')}</dt><dd>{c.website}</dd></>}
            {c.slug && <><dt>{t('ws.cp.profile')}</dt><dd><a href={`/sellers/${c.slug}`} target="_blank" rel="noreferrer">{t('ws.cp.openProfile')}</a></dd></>}
            {!c.isMember && crm.contactType && <><dt>{t('ws.priv.type')}</dt><dd>{typeLabel(contactTypes, crm.contactType, lang)}</dd></>}
            {!c.isMember && crm.source2 && <><dt>{t('ws.priv.source')}</dt><dd>{t(`ws.priv.sourceOpt.${crm.source2}`)}</dd></>}
            {!c.isMember && <><dt>{t('ws.priv.consent')}</dt><dd>{t(`ws.priv.consentType.${crm.consentType || 'none'}`)}{crm.unsubscribedAt && <span className="field-error"> · {t('ws.bulk.unsubscribed')}</span>}</dd></>}
            <dt>{t('ws.cust.lastContact')}</dt><dd>{fmtDate(crm.lastContactAt, lang, true) || '—'}</dd>
          </dl>
          {c.isMember && !c.email && !user.team?.pme && <p className="ws-hint">{t('ws.cp.privateContact')}</p>}
          <div className="ws-row"><h3 className="ws-grow">{t('ws.cp.tasks')}</h3><button type="button" className="btn btn-link btn-sm" onClick={() => setModal('task')}>+ {t('ws.tasks.add')}</button></div>
          <ul className="ws-list">
            {d.tasks.map((x) => (
              <li key={x.id} className={x.done ? 'muted' : ''}>
                <input type="checkbox" checked={x.done} onChange={() => task(x, { done: !x.done })} aria-label={x.title} />
                <span className="ws-grow">{x.title}</span><span className="small">{fmtDate(x.dueOn, lang)}</span>
              </li>
            ))}
            {!d.tasks.length && <li className="muted small">{t('ws.cp.noTasks')}</li>}
          </ul>
        </aside>
      </div>
      {modal === 'email' && <EmailComposer crmId={crm.id} customer={c} pme={!!user.team?.pme} mailbox={d.mailbox} onClose={() => setModal(null)} onSent={(r) => { setModal(null); say(r.via === 'own' ? t('ws.mail.sentOwn', { email: r.from }) : t('ws.mail.sent', { replyTo: r.replyTo })); load(); }} />}
      {modal === 'note' && <NoteModal crmId={crm.id} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === 'task' && <TaskModal crmId={crm.id} assignees={d.assignees} canAssign={d.canAssign} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === 'edit' && (
        <NewContactModal
          editId={crm.id} canAssign={false} assignees={[]}
          initial={{
            companyName: c.company || '', contactName: c.contact || '', title: c.title || '', emails: (c.emails || []).join(', '), phones: (c.phones || []).join(', '),
            whatsapp: c.whatsapp || '', website: c.website || '', country: c.country || '', city: c.city || '', address: c.address || '', lang: c.lang || 'fr',
            sector: crm.sector || '', contactType: crm.contactType || '', source: crm.source2 || '', tags: crm.tags.join(', '), consentType: crm.consentType || 'none',
          }}
          onClose={() => setModal(null)} onDone={() => { setModal(null); say(t('common.saved')); load(); }}
        />
      )}
      {modal === 'link' && <LinkMemberModal crmId={crm.id} onClose={() => setModal(null)} onDone={() => { setModal(null); say(t('ws.priv.linked')); load(); }} />}
      {modal === 'showcase' && <ShowcaseModal crmId={crm.id} onClose={() => setModal(null)} onSent={(link) => { setModal(null); say(t('ws.showcase.sent')); load(); }} />}
    </div>
  );
}

// "Lier au membre inscrit": search the registered-members directory and
// point this private contact's crm_id at one, in place (keeps its history).
function LinkMemberModal({ crmId, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const tm = setTimeout(() => api(`/workspace/candidates?q=${encodeURIComponent(q)}&all=1`).then(setList).catch((e) => setError(errorText(e))), 250);
    return () => clearTimeout(tm);
  }, [q, errorText]);
  async function link(memberId) {
    try { await api(`/workspace/customers/${crmId}`, { method: 'PATCH', body: { linkToMemberId: memberId } }); onDone(); } catch (e) { setError(errorText(e)); }
  }
  return (
    <Modal title={t('ws.priv.linkTitle')} onClose={onClose}>
      <label className="field"><span className="field-label">{t('ws.cust.search')}</span><input value={q} onChange={(e) => setQ(e.target.value)} autoFocus /></label>
      <Alert>{error}</Alert>
      {!list ? <p className="muted">{t('common.loading')}</p> : (
        <ul className="ws-list">
          {list.map((m) => (
            <li key={m.id}><span className="ws-grow">{m.company}</span><button type="button" className="btn btn-primary btn-sm" onClick={() => link(m.id)}>{t('ws.priv.linkTo')}</button></li>
          ))}
          {!list.length && <li className="muted small">{t('ws.cust.noCandidates')}</li>}
        </ul>
      )}
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button></div>
    </Modal>
  );
}

// "Partager ma vitrine": my company page + chosen products, with a
// "Demander un prix" button on each that creates a request for me — no
// sign-in needed by the contact.
function ShowcaseModal({ crmId, onClose, onSent }) {
  const { t, errorText } = useI18n();
  const [products, setProducts] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api('/my/listings').then((rows) => setProducts(rows.filter((r) => r.status === 'approved'))).catch(() => setProducts([])); }, []);
  async function send() {
    setBusy(true); setError('');
    try { const r = await api(`/workspace/customers/${crmId}/showcase`, { method: 'POST', body: { productIds: [...sel], message } }); onSent(r.link); } catch (e) { setError(errorText(e)); setBusy(false); }
  }
  return (
    <Modal title={t('ws.showcase.title')} onClose={onClose} wide>
      <label className="field ws-full"><span className="field-label">{t('ws.showcase.message')}</span><textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('ws.showcase.messagePh')} /></label>
      <p className="ws-hint">{t('ws.showcase.pickHint')}</p>
      {!products ? <p className="muted">{t('common.loading')}</p> : (
        <ul className="ws-list" style={{ maxHeight: 280, overflow: 'auto' }}>
          {products.map((p) => (
            <li key={p.id}>
              <input type="checkbox" checked={sel.has(p.id)} onChange={() => setSel((s) => { const n = new Set(s); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })} aria-label={p.title} />
              <span className="ws-grow">{p.title}</span>
            </li>
          ))}
          {!products.length && <li className="muted small">{t('ws.showcase.noProducts')}</li>}
        </ul>
      )}
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={send}>{busy ? t('common.sending') : t('ws.showcase.send')}</button></div>
    </Modal>
  );
}

function NoteModal({ crmId, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  async function save() { try { await api(`/workspace/customers/${crmId}/notes`, { method: 'POST', body: { body } }); onDone(); } catch (e) { setError(errorText(e)); } }
  return (
    <Modal title={t('ws.cp.note')} onClose={onClose}>
      <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} aria-label={t('ws.cp.note')} />
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="button" className="btn btn-primary" disabled={!body.trim()} onClick={save}>{t('common.save')}</button></div>
    </Modal>
  );
}

export function TaskModal({ crmId = null, assignees = [], canAssign = false, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [to, setTo] = useState(String(user.person?.id || ''));
  const [error, setError] = useState('');
  async function save() {
    try { await api('/workspace/tasks', { method: 'POST', body: { title, dueOn: due || null, crmId, ...(canAssign ? { assignedTo: Number(to) } : {}) } }); onDone(); } catch (e) { setError(errorText(e)); }
  }
  return (
    <Modal title={t('ws.tasks.add')} onClose={onClose}>
      <label className="field"><span className="field-label">{t('ws.tasks.what')}</span><input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} /></label>
      <label className="field"><span className="field-label">{t('ws.tasks.due')}</span><input type="date" value={due} onChange={(e) => setDue(e.target.value)} /><span className="field-hint">{t('ws.tasks.dueHint')}</span></label>
      {canAssign && <label className="field"><span className="field-label">{t('ws.cust.assignee')}</span><select value={to} onChange={(e) => setTo(e.target.value)}>{assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>}
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="button" className="btn btn-primary" disabled={!title.trim()} onClick={save}>{t('common.save')}</button></div>
    </Modal>
  );
}

// A small rich-text box (bold, italic, list, link) — sanitized by the server.
export function RichBox({ value, onChange, placeholder }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ''; }, [value]);
  const cmd = (c, arg) => { ref.current?.focus(); document.execCommand(c, false, arg); onChange(ref.current.innerHTML); };
  return (
    <div className="ws-editor">
      <div className="ws-editor-bar">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('bold')} aria-label="Bold"><b>B</b></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('italic')} aria-label="Italic"><i>I</i></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('insertUnorderedList')} aria-label="List">•</button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { const u = window.prompt('https://'); if (u) cmd('createLink', /^https?:\/\//.test(u) ? u : `https://${u}`); }} aria-label="Link">🔗</button>
      </div>
      <div ref={ref} className="ws-editor-area" contentEditable suppressContentEditableWarning data-placeholder={placeholder} onInput={() => onChange(ref.current.innerHTML)} role="textbox" aria-multiline="true" />
    </div>
  );
}

// Own mailbox connected: sent from it; otherwise the site's address (or refused when the company requires it).
export function MailboxBanner({ mailbox }) {
  const { t } = useI18n();
  if (!mailbox) return null;
  if (mailbox.connected) return <p className="callout callout-success small">{t('mailbox.banner.connected')}</p>;
  return (
    <p className={`callout ${mailbox.required ? 'callout-warning' : 'callout-info'} small`}>
      {t(mailbox.required ? 'mailbox.banner.required' : 'mailbox.banner.connect')} <Link to="/account/mailbox">{t('mailbox.banner.link')} →</Link>
    </p>
  );
}

function EmailComposer({ crmId, customer, pme, mailbox, onClose, onSent }) {
  const { t, errorText } = useI18n();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [product, setProduct] = useState('');
  const [files, setFiles] = useState([]);
  const [bcc, setBcc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api('/workspace/email-templates').then(setTemplates).catch(() => {}); }, []);
  const useTpl = (id) => { const x = templates.find((y) => String(y.id) === id); if (x) { setSubject(x.subject); setHtml(x.html); } };
  async function send() {
    setBusy(true); setError('');
    const fd = new FormData();
    fd.append('subject', subject); fd.append('html', html); fd.append('product', product); fd.append('bccOwner', bcc ? 'true' : 'false');
    files.forEach((f) => fd.append('files', f));
    try { const r = await api(`/workspace/customers/${crmId}/email`, { method: 'POST', form: fd }); onSent(r); } catch (e) { setError(errorText(e)); setBusy(false); }
  }
  return (
    <Modal title={t('ws.mail.title', { company: customer.company })} onClose={onClose} wide>
      <MailboxBanner mailbox={mailbox} />
      <div className="ws-form">
        <label className="field"><span className="field-label">{t('ws.mail.template')}</span>
          <select defaultValue="" onChange={(e) => useTpl(e.target.value)}><option value="">—</option>{templates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.lang.toUpperCase()})</option>)}</select></label>
        <label className="field"><span className="field-label">{t('ws.mail.product')}</span><input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="{product}" /></label>
        <label className="field ws-full"><span className="field-label">{t('ws.mail.subject')}</span><input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255} /></label>
        <div className="ws-full"><RichBox value={html} onChange={setHtml} placeholder={t('ws.mail.bodyPh')} /></div>
        <p className="ws-hint ws-full">{t('ws.mail.placeholders')}</p>
        <label className="field ws-full"><span className="field-label">{t('ws.mail.attachments')}</span>
          <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setFiles([...e.target.files].slice(0, 5))} /><span className="field-hint">{t('ws.mail.attachHint')}</span></label>
        {!pme && <label className="field-checkbox ws-full"><input type="checkbox" checked={bcc} onChange={(e) => setBcc(e.target.checked)} /><span>{t('ws.mail.bcc')}</span></label>}
        {!mailbox?.connected && <p className="ws-hint ws-full">{t('ws.mail.from', { name: `${user.name} – ${user.companyName}` })} {t('ws.mail.replyNote', { email: user.email })}</p>}
      </div>
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy || !subject.trim() || !html.replace(/<[^>]*>/g, '').trim()} onClick={send}>{busy ? t('common.sending') : t('ws.mail.send')}</button></div>
    </Modal>
  );
}
